import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const fmt = n => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => {
  if (!d) return '-';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/^(\d+) (\w+) (\d+)$/, '$1 $2, $3');
};

export default function Ledger() {
  const [searchParams] = useSearchParams();
  const [consumers, setConsumers] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [filters, setFilters] = useState({ barangay_id: '', search: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 25;
  const [showAll, setShowAll] = useState(false);

  // Ledger modal
  const [ledgerModal, setLedgerModal] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ reading_date: '', meter_reading: '' });
  const [saving, setSaving] = useState(false);

  // Add reading
  const [showAdd, setShowAdd] = useState(false);
  const [addData, setAddData] = useState({ reading_date: '', meter_reading: '' });
  const [adding, setAdding] = useState(false);

  const loadConsumers = (p, all) => {
    setLoading(true);
    const useAll = all !== undefined ? all : showAll;
    const noPagination = useAll || filters.barangay_id;
    const params = noPagination ? { per_page: 99999 } : { page: p || page, per_page: perPage };
    if (filters.barangay_id) params.barangay_id = filters.barangay_id;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    api.get('/consumers', { params }).then(r => {
      setConsumers(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.last_page);
      setPage(r.data.page);
      setLoading(false);
    });
  };

  useEffect(() => { api.get('/barangays').then(r => setBarangays(r.data)); }, []);
  useEffect(() => { loadConsumers(1, showAll); }, [filters.barangay_id, filters.status, showAll]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { loadConsumers(1, showAll); }, 300);
    return () => clearTimeout(t);
  }, [filters.search]); // eslint-disable-line

  // Auto-open ledger if consumer_id in URL
  useEffect(() => {
    const cid = searchParams.get('consumer_id');
    if (cid) openLedger(parseInt(cid));
  }, []); // eslint-disable-line

  const goPage = (p) => { if (p >= 1 && p <= totalPages) loadConsumers(p); };

  const statusBadge = s => {
    const map = { Active: 'badge-green', Inactive: 'badge-gray', Disconnected: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const billStatusBadge = s => {
    const map = { Paid: 'badge-green', Unpaid: 'badge-orange', Overdue: 'badge-red', Partial: 'badge-blue' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const openLedger = (consumerId) => {
    setLedgerLoading(true);
    setLedgerModal({ consumer: null, ledger: [], consumerId });
    setEditId(null);
    setShowAdd(false);
    api.get(`/consumers/${consumerId}/ledger`).then(r => {
      setLedgerModal({ ...r.data, consumerId });
      setLedgerLoading(false);
    });
  };

  const reloadLedger = () => {
    if (!ledgerModal?.consumerId) return;
    setLedgerLoading(true);
    setEditId(null);
    api.get(`/consumers/${ledgerModal.consumerId}/ledger`).then(r => {
      setLedgerModal({ ...r.data, consumerId: ledgerModal.consumerId });
      setLedgerLoading(false);
    });
  };

  // Edit
  const startEdit = (l) => {
    setEditId(l.reading_id);
    setEditData({ reading_date: l.reading_date, meter_reading: String(l.current_reading) });
    setShowAdd(false);
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async () => {
    if (!editData.reading_date || !editData.meter_reading) return;
    setSaving(true);
    try {
      await api.put(`/readings/${editId}`, {
        reading_date: editData.reading_date,
        meter_reading: parseInt(editData.meter_reading, 10),
      });
      reloadLedger();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to update reading');
    }
    setSaving(false);
  };

  // Delete
  const deleteReading = async (l) => {
    if (!window.confirm(`Delete reading on ${fmtDate(l.reading_date)}?\n\nMeter reading: ${l.current_reading}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/readings/${l.reading_id}`);
      reloadLedger();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete reading');
    }
  };

  // Add
  const openAdd = () => {
    setShowAdd(true);
    setEditId(null);
    setAddData({ reading_date: new Date().toISOString().split('T')[0], meter_reading: '' });
  };

  const cancelAdd = () => { setShowAdd(false); };

  const saveAdd = async () => {
    if (!addData.reading_date || !addData.meter_reading) return;
    setAdding(true);
    try {
      await api.post('/readings', {
        consumer_id: ledgerModal.consumerId,
        reading_date: addData.reading_date,
        meter_reading: parseInt(addData.meter_reading, 10),
      });
      setShowAdd(false);
      reloadLedger();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to add reading');
    }
    setAdding(false);
  };

  const printLedger = () => {
    if (!ledgerModal) return;
    const { consumer, ledger } = ledgerModal;
    const w = window.open('', '_blank');
    const rows = ledger.map(l => `
      <tr>
        <td>${fmtDate(l.reading_date)}</td>
        <td style="text-align:right">${l.previous_reading ?? '-'}</td>
        <td style="text-align:right">${l.current_reading}</td>
        <td style="text-align:right">${l.consumption}</td>
        <td style="text-align:right">${fmt(l.bill_amount)}</td>
        <td style="text-align:right">${fmt(l.paid_amount)}</td>
        <td style="text-align:right">${fmt(l.balance)}</td>
        <td>${l.status || '-'}</td>
      </tr>
    `).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Meter Reading Ledger</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        h2, h3 { margin: 4px 0; }
        .info { margin: 8px 0 16px; color: #555; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 5px 8px; }
        th { background: #f0f0f0; text-align: left; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      <h2>Meter Reading Ledger</h2>
      <h3>${consumer.name}</h3>
      <div class="info">${consumer.account_code} &bull; ${consumer.barangay_name} &bull; ${consumer.consumer_type}</div>
      <table>
        <thead><tr><th>Date</th><th style="text-align:right">Prev</th><th style="text-align:right">Current</th><th style="text-align:right">Cons.</th><th style="text-align:right">Billed</th><th style="text-align:right">Paid</th><th style="text-align:right">Balance</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.print();</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <h1 className="page-title" style={{margin:'0 0 16px'}}>Meter Reading Ledger</h1>

      <div className="filters">
        <input className="input" style={{width:200}} placeholder="Search..." value={filters.search}
          onChange={e => setFilters(f => ({...f, search: e.target.value}))}
          onKeyDown={e => e.key === 'Enter' && loadConsumers(1)} />
        <select className="input" style={{width:180}} value={filters.barangay_id} onChange={e => setFilters(f => ({...f, barangay_id: e.target.value}))}>
          <option value="">All Barangays</option>
          {barangays.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="input" style={{width:150}} value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Disconnected">Disconnected</option>
        </select>
      </div>

      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <>
          <div className="card table-wrap">
            <table>
              <thead><tr><th>Account</th><th>Name</th><th>Barangay</th><th>Type</th><th>Status</th></tr></thead>
              <tbody>
                {consumers.map(c => (
                  <tr key={c.id} onClick={() => openLedger(c.id)} style={{ cursor: 'pointer' }}>
                    <td>{c.account_code}</td>
                    <td>{c.name}</td>
                    <td>{c.barangay_name}</td>
                    <td>{c.consumer_type}</td>
                    <td>{statusBadge(c.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{color:'#666', fontSize:13}}>{total} consumers — click a row to view ledger</span>
            {!filters.barangay_id && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {!showAll && (
                  <>
                    <button className="btn btn-sm" style={{background:'#eee'}} disabled={page <= 1} onClick={() => goPage(page - 1)}>Prev</button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let p;
                      if (totalPages <= 7) p = i + 1;
                      else if (page <= 4) p = i + 1;
                      else if (page >= totalPages - 3) p = totalPages - 6 + i;
                      else p = page - 3 + i;
                      return (
                        <button key={p} className="btn btn-sm" onClick={() => goPage(p)}
                          style={{ background: p === page ? '#1976d2' : '#eee', color: p === page ? 'white' : '#333', minWidth: 32 }}>
                          {p}
                        </button>
                      );
                    })}
                    <button className="btn btn-sm" style={{background:'#eee'}} disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next</button>
                  </>
                )}
                <button className="btn btn-sm" style={{background: showAll ? '#1976d2' : '#eee', color: showAll ? 'white' : '#333'}}
                  onClick={() => setShowAll(a => !a)}>
                  {showAll ? 'Paginate' : 'Show All'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Ledger Modal */}
      {ledgerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setLedgerModal(null)}>
          <div className="card" style={{ width: '95%', maxWidth: 1050, maxHeight: '85vh', margin: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            {ledgerLoading ? (
              <div className="loading" style={{ padding: 40 }}><div className="spinner"></div></div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{ledgerModal.consumer?.name}</h3>
                    <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                      {ledgerModal.consumer?.account_code} &bull; {ledgerModal.consumer?.barangay_name} &bull; {ledgerModal.consumer?.consumer_type}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff'}} onClick={openAdd}>+ Add Reading</button>
                    <button className="btn btn-primary btn-sm" onClick={printLedger}>Print</button>
                    <button className="btn btn-sm" style={{ background: '#eee', fontSize: 18, lineHeight: 1, padding: '4px 10px' }} onClick={() => setLedgerModal(null)}>&times;</button>
                  </div>
                </div>

                {/* Add Reading Form */}
                {showAdd && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: '#e8f5e9', borderRadius: 6, marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>New Reading:</span>
                    <input type="date" className="input" style={{ width: 160, padding: '6px 8px', fontSize: 13 }} value={addData.reading_date}
                      onChange={e => setAddData(d => ({...d, reading_date: e.target.value}))} />
                    <input type="number" className="input" placeholder="Meter reading" style={{ width: 130, padding: '6px 8px', fontSize: 13 }} value={addData.meter_reading}
                      onChange={e => setAddData(d => ({...d, meter_reading: e.target.value}))} min="0" />
                    <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff',padding:'6px 16px',fontSize:13}} onClick={saveAdd} disabled={adding}>{adding ? 'Saving...' : 'Save'}</button>
                    <button className="btn btn-sm" style={{background:'#eee',padding:'6px 16px',fontSize:13}} onClick={cancelAdd}>Cancel</button>
                  </div>
                )}

                <div style={{ overflow: 'auto', flex: 1 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">Prev</th>
                        <th className="text-right">Current</th>
                        <th className="text-right">Consumption</th>
                        <th className="text-right">Billed</th>
                        <th className="text-right">Paid</th>
                        <th className="text-right">Balance</th>
                        <th>Status</th>
                        <th style={{width:120, textAlign:'center'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerModal.ledger?.length === 0 ? (
                        <tr><td colSpan="9" style={{ textAlign: 'center', color: '#999' }}>No readings found</td></tr>
                      ) : ledgerModal.ledger?.map((l, i) => (
                        editId === l.reading_id ? (
                          <tr key={i} style={{background:'#fffbe6'}}>
                            <td><input type="date" className="input" style={{width:140, padding:'4px 6px', fontSize:13}} value={editData.reading_date} onChange={e => setEditData(d => ({...d, reading_date: e.target.value}))} /></td>
                            <td className="text-right">{l.previous_reading ?? '-'}</td>
                            <td className="text-right"><input type="number" className="input" style={{width:90, padding:'4px 6px', fontSize:13, textAlign:'right'}} value={editData.meter_reading} onChange={e => setEditData(d => ({...d, meter_reading: e.target.value}))} min="0" /></td>
                            <td className="text-right">{l.consumption}</td>
                            <td className="text-right">PHP {fmt(l.bill_amount)}</td>
                            <td className="text-right">PHP {fmt(l.paid_amount)}</td>
                            <td className="text-right" style={{ fontWeight: 600, color: l.balance > 0 ? '#d32f2f' : '#2e7d32' }}>PHP {fmt(l.balance)}</td>
                            <td>{l.status ? billStatusBadge(l.status) : '-'}</td>
                            <td style={{textAlign:'center'}}>
                              <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff',marginRight:4,padding:'3px 10px',fontSize:12}} onClick={saveEdit} disabled={saving}>{saving ? '...' : 'Save'}</button>
                              <button className="btn btn-sm" style={{background:'#eee',padding:'3px 10px',fontSize:12}} onClick={cancelEdit}>Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={i}>
                            <td>{fmtDate(l.reading_date)}</td>
                            <td className="text-right">{l.previous_reading ?? '-'}</td>
                            <td className="text-right">{l.current_reading}</td>
                            <td className="text-right">{l.consumption}</td>
                            <td className="text-right">PHP {fmt(l.bill_amount)}</td>
                            <td className="text-right">PHP {fmt(l.paid_amount)}</td>
                            <td className="text-right" style={{ fontWeight: 600, color: l.balance > 0 ? '#d32f2f' : '#2e7d32' }}>PHP {fmt(l.balance)}</td>
                            <td>{l.status ? billStatusBadge(l.status) : '-'}</td>
                            <td style={{textAlign:'center'}}>
                              <button className="btn btn-sm" style={{background:'#1976d2',color:'#fff',marginRight:4,padding:'3px 10px',fontSize:12}} onClick={() => startEdit(l)}>Edit</button>
                              <button className="btn btn-sm" style={{background:'#d32f2f',color:'#fff',padding:'3px 10px',fontSize:12}} onClick={() => deleteReading(l)}>Delete</button>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
